import Carousel from '../../design-system/carousel/with-arrows';
import MediaLoader from '../../design-system/media-loader';
import isMobileBrowser from '../../utils/isMobileBrowser';

import {
  GALLERY_SELECTOR,
  MEDIA_SELECTOR,
  THUMBNAIL_SELECTOR,
  LEFT_ARROW_SELECTOR,
  RIGHT_ARROW_SELECTOR,
  FULL_PAGE_GALLERY,
  FULL_PAGE_GALLERY_CLOSE,
  FULL_PAGE_GALLERY_WRAPPER
} from '../../classnames/above-the-fold-gallery';

import { FULL_PAGE_GALLERY_VISIBLE } from '../../classnames/third-party-buttons';

import { HIDDEN, IMAGE_CONTAINER } from '../../classnames/design-system';

import {
  LAZYLOAD_CLASS_NAME,
  LAZYLOADED_CLASS_NAME,
  IMAGE_SELECTOR,
  LAZYLOAD_IMAGE_ATTRIBUTE
} from '../../classnames/lazyload';

import { common, mediaQueries } from '../../variables';
import { ZOOMED_ATTRIBUTE } from '../../design-system/carousel/constants';

export default class GalleryPopup {
  galleryMaxWidth = 1024;
  placeholderTag = 'div';
  galleryContainer = null;

  constructor(activeElements, gallery, container) {
    this.activeElements = [...activeElements];
    this.container = container;
    this.galleryContainer = container.querySelector(FULL_PAGE_GALLERY);

    if (gallery) {
      this.gallery = gallery;
    }
  }

  showPopup = () => {
    this.galleryContainer.classList.remove(HIDDEN);
    document.body.classList.add(FULL_PAGE_GALLERY_VISIBLE);
  };

  hidePopup = () => {
    this.galleryContainer.classList.add(HIDDEN);
    this.galleryContainer.removeAttribute(ZOOMED_ATTRIBUTE);
    document.body.classList.remove(FULL_PAGE_GALLERY_VISIBLE);
  };

  cleanupPopup = () => {
    this.galleryWrapper().innerHTML = `<${this.placeholderTag}></${this.placeholderTag}>`;
  };

  setPopupGallery = (gallery) => {
    this.galleryWrapper().querySelector(this.placeholderTag).replaceWith(gallery);
  };

  galleryWrapper = () => this.galleryContainer.querySelector(FULL_PAGE_GALLERY_WRAPPER);

  initializeCarousel = () => {
    const carousel = new Carousel({
      galleryContainer: this.galleryContainer,
      containerSelector: `${FULL_PAGE_GALLERY} ${GALLERY_SELECTOR}`,
      elementSelector: MEDIA_SELECTOR,
      previousSelector: LEFT_ARROW_SELECTOR,
      nextSelector: RIGHT_ARROW_SELECTOR,
      thumbnailSelector: THUMBNAIL_SELECTOR
    });

    carousel.initializeEventListeners();

    return carousel;
  };

  mousemoveEventHandler = (e) => {
    const container = e.target.parentNode;
    const containerScrollHeight = container.scrollHeight;
    const viewportHeight = window.innerHeight;
    const isScrollNeeded = !(viewportHeight - common.gallerySelectorHeight >= containerScrollHeight);

    if (!isScrollNeeded) return;

    const scrollableHeight = viewportHeight - containerScrollHeight;
    const cursorPositionInPercent = ((e.clientY - 150) / viewportHeight) * 100;
    const scrollYAmount = (Math.round((scrollableHeight * cursorPositionInPercent * 2.2) / 100));

    container.style.transform = `translateY(${scrollYAmount}px)`;
  };

  toggleGalleryZoom = () => {
    const images = [...this.galleryContainer.querySelectorAll(`.${MEDIA_SELECTOR} .${IMAGE_CONTAINER}`)];

    if (this.galleryContainer.getAttribute(ZOOMED_ATTRIBUTE)) {
      images.forEach((imageContainer) => {
        imageContainer.style.maxWidth = `${this.galleryWidth}px`;
        imageContainer.style.width = 'auto';

        const imageElement = imageContainer.querySelector('img');

        if (imageElement) {
          imageElement.parentNode.style.transform = 'unset';
        }
      });

      this.galleryContainer.scrollTop = 0;
      this.galleryContainer.scrollLeft = 0;
      this.galleryContainer.removeAttribute(ZOOMED_ATTRIBUTE);
    } else {
      images.forEach((imageContainer) => {
        imageContainer.style.maxWidth = `${this.galleryMaxWidth}px`;
        imageContainer.style.width = `${this.galleryMaxWidth}px`;

        const imageNode = imageContainer.querySelector('img');

        if (imageNode) {
          imageNode.addEventListener('mousemove', this.mousemoveEventHandler);

          if (isMobileBrowser()) {
            imageNode.style.maxHeight = 'unset';
            imageNode.style.height = '120%';
          }
        }
      });

      this.galleryContainer.scrollTop = (this.galleryMaxWidth - window.innerHeight) / 2;
      this.galleryContainer.scrollLeft = (this.galleryMaxWidth - window.innerWidth) / 2;
      this.galleryContainer.setAttribute(ZOOMED_ATTRIBUTE, true);
    }
  };

  initializeMediaLoadListeners = (gallery) => {
    [...gallery.querySelectorAll(IMAGE_SELECTOR)].forEach((image) => {
      image.classList.remove(LAZYLOADED_CLASS_NAME);
      image.classList.add(LAZYLOAD_CLASS_NAME);
    });

    (new MediaLoader()).initialize(gallery);
  };

  initializeZoomListeners = (gallery) => {
    const images = [...this.galleryContainer.querySelectorAll(`.${MEDIA_SELECTOR} .${IMAGE_CONTAINER}`)];

    if (isMobileBrowser()) {
      [...gallery.querySelectorAll(`.${MEDIA_SELECTOR}`)].forEach((element) => {
        element.addEventListener('click', this.toggleGalleryZoom);
      });
    }

    images.forEach((image) => {
      image.style.maxWidth = `${this.galleryMaxWidth}px`;

      const imageNode = image.querySelector('img');

      if (imageNode) {
        imageNode.addEventListener('mousemove', this.mousemoveEventHandler);
      }
    });
  };

  initializeKeyboardListeners = (carousel) => {
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') {
        this.handleHide();
      }

      if (e.key === 'ArrowRight') {
        carousel.selectNext();
      }

      if (e.key === 'ArrowLeft') {
        carousel.selectPrevious();
      }
    });
  };

  handleShow = ({ currentTarget }) => {
    const gallery = (this.gallery || currentTarget.closest(GALLERY_SELECTOR)).cloneNode(true);

    const fullImages = [...gallery
      .querySelector('.above-the-fold-v3__gallery-media-wrapper')
      .querySelectorAll('.js-variant-image-container')]
      .map((imgContainer) => imgContainer.querySelector('img'));

    fullImages.forEach((img) => {
      const { originalImage, src } = img.dataset;

      img.setAttribute(LAZYLOAD_IMAGE_ATTRIBUTE, (originalImage || src).replace('_640x', ''));
    });

    this.setPopupGallery(gallery);

    const carousel = this.initializeCarousel();

    this.initializeMediaLoadListeners(gallery);
    this.initializeZoomListeners(gallery);

    if (document.body.clientWidth >= mediaQueries['1024px']) {
      this.initializeKeyboardListeners(carousel);
    }

    this.showPopup();
  };

  handleHide = () => {
    this.hidePopup();
    this.cleanupPopup();
  };

  initializeEventListeners = () => {
    const closeIcon = this.container.querySelector(FULL_PAGE_GALLERY_CLOSE);
    this.activeElements.forEach((element) => element.addEventListener('click', this.handleShow));

    if (closeIcon) {
      closeIcon.addEventListener('click', this.handleHide);
    }
  };
}
